import { useState, useEffect } from 'react';
import { EditIcon } from '../../../../assets/icons/EditIcon';
import { DrawerIcon } from '../../../../assets/icons/DrawerIcon';
import { SaveIcon } from '../../../../assets/icons/SaveIcon';

const WalletEditSidebar = ({ wallet, isOpen, onClose, onSave }) => {
    const [editedBalance, setEditedBalance] = useState('');
    const [error, setError] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (wallet) {
            setEditedBalance(wallet.balance.toString());
            setError('');
            setIsEditMode(false);
        }
    }, [wallet]);

    const handleEditClick = () => {
        setIsEditMode(true);
        setError('');
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEditedBalance(wallet.balance.toString());
        setError('');
    };

    const handleSave = async () => {
        const numericBalance = parseFloat(editedBalance);
        
        if (isNaN(numericBalance)) {
            setError('Please enter a valid number');
            return;
        }
        
        if (numericBalance < 0) {
            setError('Balance cannot be negative');
            return;
        }
        
        setIsSaving(true);
        setError('');
        
        try {
            await onSave(wallet.id, numericBalance);
            setIsEditMode(false);
        } catch (err) {
            setError('Failed to update balance. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    if (!wallet) return null;

    return (
        <div className={`fixed inset-0 z-50 overflow-hidden ${isOpen ? '' : 'pointer-events-none'}`}>
            {/* Sidebar Panel */}
            <div className={`fixed right-0 top-0 h-full w-[580px] bg-eerie-black shadow-xl flex flex-col transform transition-transform duration-300 ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}>
                {/* Header */}
                <div className="flex flex-col">
                    <div className='border-b border-zinc-700'>
                        <button
                            onClick={onClose}
                            className="mx-4 my-3 bg-charleston-green rounded-lg transition-colors hover:bg-zinc-800"
                        >
                            <DrawerIcon width={38} height={34} />
                        </button>
                    </div>

                    <div className="flex flex-col px-4 py-3">
                        <div className="flex justify-between items-center">
                            <h2 className="text-3xl text-white">Wallet</h2>
                            {!isEditMode ? (
                                <button 
                                    onClick={handleEditClick}
                                    className="px-4 py-2 bg-charleston-green text-white border border-zinc-700 rounded-full hover:bg-zinc-800 flex items-center justify-center space-x-2"
                                >
                                    <EditIcon width={20} height={20} />
                                    <span className="text-sm">Edit Balance</span>
                                </button>
                            ) : (
                                <div className="flex space-x-2">
                                    {!isSaving && (
                                        <button 
                                            onClick={handleCancelEdit}
                                            className="px-4 py-2 bg-charleston-green text-white border border-zinc-700 rounded-full hover:bg-zinc-800 flex" 
                                        >
                                            <span className="text-sm">Cancel</span>
                                        </button>
                                    )}
                                    <button 
                                        onClick={handleSave}
                                        disabled={isSaving}
                                        className={`px-4 py-2 text-white border rounded-full flex items-center justify-center space-x-2 ${
                                            isSaving 
                                                ? "bg-gray-600 border-gray-600 cursor-not-allowed" 
                                                : "bg-green-600 border-green-600 hover:bg-green-500"
                                        }`}
                                    >
                                        {isSaving ? (
                                            <>
                                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span className="text-sm">Saving...</span>
                                            </>
                                        ) : (
                                            <>
                                                <SaveIcon width={20} height={20} />
                                                <span className="text-sm">Save Changes</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                        {isEditMode && error && (
                            <div className="mt-2 p-2 bg-red-950 border border-red-800 rounded-lg">
                                <p className="text-red-400 text-xs">{error}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto">
                    <div className="px-4 pb-4 pt-2 space-y-6">
                        {/* Wallet Card - Always Visible */}
                        <div className="bg-charleston-green border border-zinc-700 rounded-3xl p-3">
                            <div className="flex justify-between items-start">
                                <div className="flex items-center">
                                    <div className="h-14 w-14 mr-3 rounded-full bg-zinc-800 overflow-hidden flex items-center justify-center">
                                        <img 
                                            src={wallet.logo} 
                                            alt={wallet.name}
                                            className="h-12 w-12 rounded-full object-cover"
                                        />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-medium text-white">{wallet.name}</h2>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mt-3 space-y-0.5">
                                <p className="text-xs text-zinc-500 uppercase">Current Balance</p>
                                <p className="text-xl font-bold text-white">{formatCurrency(wallet.balance)}</p>
                            </div>
                        </div>

                        {/* Balance Information Section */}
                        <div>
                            <div className="flex items-center space-x-2 mb-4">
                                <h3 className="text-white font-medium">Balance Information</h3>
                            </div>

                            <div className="space-y-4">
                                {/* Balance Field */}
                                <div>
                                    <p className="text-md mb-1">Wallet Balance</p>
                                    <div className="space-y-2">
                                        <div>
                                            <p className="text-xs text-zinc-500 mb-1">
                                                {isEditMode ? 'New Balance (₹)' : 'Available Balance'}
                                            </p>
                                            {!isEditMode ? (
                                                <input
                                                    type="text"
                                                    value={formatCurrency(wallet.balance)}
                                                    readOnly
                                                    className="w-full bg-charleston-green border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                                                />
                                            ) : (
                                                <input
                                                    type="number"
                                                    value={editedBalance}
                                                    onChange={(e) => {
                                                        setEditedBalance(e.target.value);
                                                        setError('');
                                                    }}
                                                    className={`w-full bg-charleston-green border rounded-lg px-3 py-2 text-white text-sm ${
                                                        error ? 'border-red-500' : 'border-zinc-700'
                                                    } focus:border-blue-500 focus:outline-none`}
                                                    placeholder="Enter new balance"
                                                    step="0.01"
                                                />
                                            )}
                                            {error && isEditMode && (
                                                <p className="text-red-500 text-xs mt-1">{error}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WalletEditSidebar;